import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useJugadorPerfil, useJugadorStats, useHistoricoFisico } from "../hooks/useJugador";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { jugadoresAPI } from "../services/api";
import toast from "react-hot-toast";

const TABS = ["Resumen","Stats","Fisico","Wellness","Notas","Pagos"];

export default function JugadorDetallePage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [tab, setTab] = useState(0);
  const [varFisica, setVarFisica] = useState("sprint_10m_seg");
  const { data: perfil, isLoading } = useJugadorPerfil(id!);
  const { data: statsData } = useJugadorStats(id!);
  const { data: fisicoData } = useHistoricoFisico(id!);

  const descargarDossier = async () => {
    try {
      const res = await jugadoresAPI.dossierPdf(id!);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = `dossier_${id}.pdf`; link.click();
      toast.success("Dossier descargado");
    } catch { toast.error("Error al generar dossier"); }
  };

  if (isLoading) return <div style={{ padding:40, textAlign:"center" }}>Cargando...</div>;
  const j = perfil?.jugador;
  if (!j) return <div style={{ padding:40 }}>Jugador no encontrado</div>;

  const medicionesGrafica = (fisicoData?.mediciones || [])
    .map((m: any) => ({ fecha: m.fecha_medicion, valor: m[varFisica] }))
    .filter((d: any) => d.valor != null);

  const totales = statsData?.totales || {};

  return (
    <div style={{ minHeight:"100vh", background:"#F5F5F5" }}>
      {/* Header del jugador */}
      <div style={{ background:"#0D1B2A", color:"white", padding:"24px 32px" }}>
        <button onClick={()=>nav("/jugadores")} style={{ background:"none", border:"none", color:"#888", cursor:"pointer", fontSize:13, marginBottom:12 }}>← Jugadores</button>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          <div style={{ width:72, height:72, borderRadius:"50%", background:"#1C2E40", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, fontWeight:700 }}>
            {j.nombre[0]}{j.apellido[0]}
          </div>
          <div>
            <h1 style={{ fontSize:24, fontWeight:700, marginBottom:4 }}>{j.nombre} {j.apellido}</h1>
            <p style={{ color:"#AAA", fontSize:14 }}>{j.posicion_principal} · {j.categoria} · Pie {j.pie_dominante}</p>
          </div>
          <button onClick={descargarDossier}
            style={{ marginLeft:"auto", background:"#F9A825", color:"#0D1B2A", border:"none", borderRadius:8, padding:"10px 20px", fontWeight:700, cursor:"pointer" }}>
            Descargar Dossier PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background:"white", borderBottom:"1px solid #EEE", padding:"0 32px", display:"flex", gap:0 }}>
        {TABS.map((t,i) => (
          <button key={t} onClick={()=>setTab(i)}
            style={{ padding:"14px 20px", border:"none", background:"none", cursor:"pointer", fontSize:14,
                     fontWeight: tab===i?600:400, color: tab===i?"#0D1B2A":"#888",
                     borderBottom: tab===i?"2px solid #0D1B2A":"2px solid transparent" }}>
            {t}
          </button>
        ))}
      </div>

      <div style={{ padding:32, maxWidth:1100, margin:"0 auto" }}>

        {/* TAB: Resumen */}
        {tab === 0 && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:24 }}>
              {[
                { label:"Partidos", val: totales.partidos_jugados || 0 },
                { label:"Minutos", val: totales.minutos_totales || 0 },
                { label:"Goles", val: totales.goles_total || 0 },
                { label:"Asistencias", val: totales.asistencias_total || 0 },
                { label:"Rating prom.", val: totales.rating_promedio || "—" },
              ].map(k => (
                <div key={k.label} style={{ background:"white", border:"1px solid #EEE", borderRadius:10, padding:20, textAlign:"center" }}>
                  <div style={{ fontSize:28, fontWeight:700, color:"#0D1B2A" }}>{k.val}</div>
                  <div style={{ fontSize:12, color:"#888", marginTop:4 }}>{k.label}</div>
                </div>
              ))}
            </div>
            <div style={{ background:"white", borderRadius:10, padding:24, border:"1px solid #EEE" }}>
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Datos personales</h3>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, fontSize:14 }}>
                {[
                  ["Nombre completo", `${j.nombre} ${j.apellido}`],
                  ["Fecha nacimiento", j.fecha_nacimiento],
                  ["Dorsal", j.dorsal || "—"],
                  ["Estado", j.estado],
                  ["Colegio", j.colegio || "—"],
                  ["Grado", j.grado_escolar || "—"],
                ].map(([k, v]) => (
                  <div key={k as string} style={{ display:"flex", gap:8 }}>
                    <span style={{ color:"#888", minWidth:140 }}>{k}:</span>
                    <span style={{ fontWeight:500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB: Fisico */}
        {tab === 2 && (
          <div>
            <div style={{ background:"white", borderRadius:10, padding:24, border:"1px solid #EEE", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
                <h3 style={{ fontSize:15, fontWeight:600 }}>Evolucion fisica</h3>
                <select value={varFisica} onChange={e=>setVarFisica(e.target.value)}
                  style={{ padding:"8px 12px", border:"1px solid #DDD", borderRadius:8, fontSize:13 }}>
                  <option value="sprint_10m_seg">Sprint 10m (seg)</option>
                  <option value="sprint_20m_seg">Sprint 20m (seg)</option>
                  <option value="salto_vertical_cmj_cm">Salto CMJ (cm)</option>
                  <option value="peso_kg">Peso (kg)</option>
                  <option value="estatura_cm">Estatura (cm)</option>
                  <option value="vo2max_estimado">VO2max estimado</option>
                  <option value="agilidad_t_test_seg">Agilidad T-Test (seg)</option>
                  <option value="velocidad_max_kmh">Velocidad maxima (km/h)</option>
                </select>
              </div>
              {medicionesGrafica.length > 1 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={medicionesGrafica}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                    <XAxis dataKey="fecha" tick={{ fontSize:11 }} />
                    <YAxis tick={{ fontSize:11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="valor" stroke="#1565C0" strokeWidth={2} dot={{ r:4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p style={{ color:"#888", textAlign:"center", padding:40 }}>Minimo 2 mediciones para ver la grafica</p>}
            </div>
            <div style={{ background:"white", borderRadius:10, padding:24, border:"1px solid #EEE" }}>
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Historial de mediciones</h3>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ background:"#F5F5F5" }}>
                      {["Fecha","Estatura","Peso","Sprint 10m","Salto CMJ","VO2max","Notas"].map(h => (
                        <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontWeight:500, color:"#666", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(fisicoData?.mediciones || []).map((m: any, i: number) => (
                      <tr key={m.id} style={{ borderTop:"1px solid #F0F0F0", background: i%2===0?"white":"#FAFAFA" }}>
                        <td style={{ padding:"10px 12px" }}>{m.fecha_medicion}</td>
                        <td style={{ padding:"10px 12px" }}>{m.estatura_cm ? `${m.estatura_cm} cm` : "—"}</td>
                        <td style={{ padding:"10px 12px" }}>{m.peso_kg ? `${m.peso_kg} kg` : "—"}</td>
                        <td style={{ padding:"10px 12px" }}>{m.sprint_10m_seg ? `${m.sprint_10m_seg} s` : "—"}</td>
                        <td style={{ padding:"10px 12px" }}>{m.salto_vertical_cmj_cm ? `${m.salto_vertical_cmj_cm} cm` : "—"}</td>
                        <td style={{ padding:"10px 12px" }}>{m.vo2max_estimado || "—"}</td>
                        <td style={{ padding:"10px 12px", color:"#888", fontSize:12 }}>{m.notas || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 1 && (
          <div style={{ background:"white", borderRadius:10, padding:24, border:"1px solid #EEE" }}>
            <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Estadisticas de temporada</h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
              {[
                { l:"Goles pie derecho", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.goles_pie_derecho,0)||0 },
                { l:"Goles pie izquierdo", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.goles_pie_izquierdo,0)||0 },
                { l:"Goles cabeza", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.goles_cabeza,0)||0 },
                { l:"Recuperaciones", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.recuperaciones_propio+s.recuperaciones_contrario,0)||0 },
                { l:"Centros", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.centros,0)||0 },
                { l:"Intercepciones", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.intercepciones,0)||0 },
                { l:"Amarillas", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.tarjetas_amarillas,0)||0 },
                { l:"Rojas", v: statsData?.partidos?.reduce((a:number,s:any)=>a+s.tarjetas_rojas,0)||0 },
              ].map(k => (
                <div key={k.l} style={{ background:"#F8F8F8", borderRadius:8, padding:16, textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:700, color:"#1565C0" }}>{k.v}</div>
                  <div style={{ fontSize:11, color:"#888", marginTop:3 }}>{k.l}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
